import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../api/context';
import { useApiQuery, useRemove } from '../api/hooks';
import { Button } from '../design/primitives/Button';
import { EmptyState, ErrorBanner, Progress, Skeleton } from '../design/primitives/Misc';
import { useConfirm } from '../design/primitives/Overlay';
import { useToast } from '../design/primitives/Toast';
import { Icon, type IconName } from '../design/icons';
import { formatDateTime } from '../../lib/format';
import { ApiError } from '../api/client';
import { systemName, useDataSource } from '../meta/useDataSource';

// AttachmentsPanel: /v1/attachments?entity_type=&entity_id= list; multipart upload via
// client.upload(); drag-and-drop + picker. Opening a file asks the API for a signed link
// (:id/signed-url — for a tenant on Halo, Halo's own short-lived link) and falls back to an
// authenticated download: a plain <a href> to the API cannot carry the session and would 401.
// For a tenant served by its connected system the files are that system's: listed and opened
// here, uploaded and removed there.

export interface Attachment {
  id: string;
  filename: string;
  mime: string;
  bytes: number;
  sha256?: string;
  download_url?: string;
  created_at: string;
  uploaded_by_name?: string | null;
}

function bytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function iconFor(mime: string): IconName {
  if (mime.startsWith('image/')) return 'eye';
  if (mime === 'application/pdf') return 'file-text';
  if (mime.includes('zip') || mime.includes('compressed')) return 'package';
  return 'file';
}

export function AttachmentsPanel({ entityType, entityId, accept }: { entityType: string; entityId: string; accept?: string }) {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const key = ['attachments', entityType, entityId];
  const q = useApiQuery<{ data: Attachment[] }>(key, '/v1/attachments', { query: { entity_type: entityType, entity_id: entityId, sort: '-created_at' } });
  const [over, setOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { overlay } = useDataSource();
  const readOnly = Boolean(overlay);

  const openFile = async (a: Attachment) => {
    // Open the tab now (inside the click) so a popup blocker allows it; point it at the file once known.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const s = await api.request<{ url: string | null; direct: boolean; fallback: string | null }>(`/v1/attachments/${encodeURIComponent(a.id)}/signed-url`);
      let href = s.url;
      if (!href) {
        const blob = await api.request<Blob>(s.fallback ?? `/v1/attachments/${encodeURIComponent(a.id)}/download`, { as: 'blob' });
        href = URL.createObjectURL(blob);
        window.setTimeout(() => URL.revokeObjectURL(href!), 60_000);
      }
      if (tab) tab.location.href = href;
      else window.location.assign(href);
    } catch (e) {
      tab?.close();
      toast({ kind: 'error', title: `Could not open ${a.filename}`, detail: e instanceof ApiError ? e.detail : String(e) });
    }
  };

  const upload = useMutation({
    mutationFn: async (files: File[]) => {
      const out: unknown[] = [];
      for (const f of files) out.push(await api.upload({ entity_type: entityType, entity_id: entityId }, f));
      return out;
    },
    onSuccess: (r) => {
      toast({ title: r.length === 1 ? 'File attached' : `${r.length} files attached` });
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast({ kind: 'error', title: 'Upload failed', detail: e instanceof ApiError ? e.detail : String(e) }),
  });
  const remove = useRemove('attachments', { toast: { success: 'Attachment removed' }, onSuccess: () => void qc.invalidateQueries({ queryKey: key }) });

  const pick = (list: FileList | null) => {
    const files = Array.from(list ?? []);
    if (files.length) upload.mutate(files);
  };
  const rows = q.data?.data ?? [];

  return (
    <div className="hf-attachments">
      {readOnly ? (
        <p className="hf-muted" style={{ margin: '0 0 var(--s-2)' }}>
          Files on this record are kept in {systemName(overlay!)}; add or remove them there.
        </p>
      ) : (
      <div
        className={['hf-dropzone', over && 'over'].filter(Boolean).join(' ')}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          pick(e.dataTransfer.files);
        }}
        role="button"
        tabIndex={0}
        aria-label="Upload files"
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
      >
        <Icon name="upload" size={18} />
        <div>
          <b>Drop files here</b> or <span className="hf-link">browse</span>
        </div>
        <input ref={inputRef} type="file" multiple accept={accept} hidden onChange={(e) => pick(e.target.files)} />
        {upload.isPending && <Progress indeterminate className="bar" title="Uploading" />}
      </div>
      )}
      {q.isLoading ? (
        <Skeleton lines={3} />
      ) : q.error ? (
        <ErrorBanner error={q.error} />
      ) : rows.length === 0 ? (
        <EmptyState title="No attachments" icon="paperclip" />
      ) : (
        <ul className="hf-attach-list">
          {rows.map((a) => (
            <li key={a.id} className="hf-attach">
              <Icon name={iconFor(a.mime)} size={16} className="hf-muted" />
              <div className="hf-grow" style={{ minWidth: 0 }}>
                <button type="button" className="hf-link name hf-linkbtn" onClick={() => void openFile(a)}>
                  {a.filename}
                </button>
                <div className="hf-muted meta hf-num">
                  {bytes(a.bytes)} · {formatDateTime(a.created_at)}
                  {a.uploaded_by_name ? ` · ${a.uploaded_by_name}` : ''}
                </div>
              </div>
              <Button size="sm" variant="ghost" icon="download" aria-label={`Download ${a.filename}`} onClick={() => void openFile(a)} />
              {!readOnly && <Button
                size="sm"
                variant="ghost"
                icon="trash"
                aria-label={`Remove ${a.filename}`}
                onClick={async () => {
                  if ((await confirm({ title: `Remove ${a.filename}?`, body: 'The file is detached from this record and deleted.', confirmLabel: 'Remove', danger: true })) !== false) remove.mutate(a.id);
                }}
              />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
